import {computed, inject, Injectable, OnDestroy, signal} from '@angular/core';
import {interval, Subscription} from 'rxjs';
import {AudioService} from '../audio/audio.service';

@Injectable({
  providedIn: 'root'
})
export class SlideshowStateService implements OnDestroy {
  // State signals
  readonly slideshowIntervalDuration = signal(4000); // Default 4 seconds for images (matches slideshow-control.service.ts)
  readonly shouldLoopAudio = signal(false); // Flag to indicate if audio should loop
  readonly isVideoPlaying = signal(false); // Flag to track if a video is currently playing
  readonly userPaused = signal(false); // Track if user has manually paused playback
  readonly isTransitioning = signal(false); // Track slide transitions
  // Injected services
  private audioService = inject(AudioService);
  // Computed properties
  readonly isPlaying = computed(() => this.audioService.isPlaying());
  readonly currentTime = computed(() => this.audioService.currentTime());
  // Private state
  private audioMonitorInterval: any = null; // Interval for monitoring audio playback
  private heartbeatCheckInterval: any = null; // Interval for state synchronization heartbeat
  private wasPlayingBeforeHidden = false; // Track if audio was playing before page was hidden
  private pausedDueToTabSwitch = false; // Track if pause was due to tab switching
  private visibilityHandler: any = null; // Handler for visibility change events
  private pageHideHandler: any = null; // Handler for page hide events
  private beforeUnloadHandler: any = null; // Handler for before unload events
  // Slideshow interval management
  private slideshowInterval: Subscription | null = null;

  constructor() {
    // Set up page visibility and unload event handlers
    this.setupPageVisibilityHandlers();
  }

  ngOnDestroy(): void {
    this.stopAudioMonitoring();
    this.stopHeartbeatCheck();
    this.removePageVisibilityHandlers();
    this.pauseSlideshow();
  }

  /**
   * Start slideshow timing management
   * @param mediaItems Array of media items with duration information
   * @param currentIndex Current media index
   * @param onAdvance Callback when slideshow should advance
   */
  startSlideshow(
    mediaItems: { url: string; type: 'image' | 'video'; duration?: number }[],
    currentIndex: number,
    onAdvance: () => void
  ): void {
    // Stop any existing slideshow
    this.pauseSlideshow();

    if (mediaItems.length === 0) {
      console.log('No media items to display in slideshow');
      return;
    }

    const currentMedia = mediaItems[currentIndex];
    if (!currentMedia) {
      console.log('Current media item not found, stopping slideshow');
      return;
    }

    let intervalDuration = this.slideshowIntervalDuration();

    // For videos, use their duration if available, otherwise use a longer default
    if (currentMedia.type === 'video') {
      if (currentMedia.duration && currentMedia.duration > 0) {
        intervalDuration = currentMedia.duration * 1000; // Convert to milliseconds
        console.log(`Using video duration: ${currentMedia.duration}s`);
      } else {
        intervalDuration = 10000; // 10 seconds default for videos without duration
        console.log('Using default video duration: 10s');
      }
    } else {
      // For images, use the configured interval
      console.log(`Using image interval: ${intervalDuration}ms`);
    }

    console.log(`Starting slideshow with interval: ${intervalDuration}ms`);

    // Create new interval
    this.slideshowInterval = interval(intervalDuration).subscribe(() => {
      console.log('Slideshow interval triggered, advancing to next media');
      onAdvance();
    });
  }

  /**
   * Pause the slideshow
   */
  pauseSlideshow(): void {
    if (this.slideshowInterval) {
      console.log('Pausing slideshow');
      this.slideshowInterval.unsubscribe();
      this.slideshowInterval = null;
    }
  }

  /**
   * Calculate if audio should loop based on total slideshow duration
   * @param mediaItems Array of media items to calculate total duration
   */
  calculateAudioLoopRequirement(mediaItems: { url: string; type: 'image' | 'video'; duration?: number }[]): void {
    if (mediaItems.length === 0) {
      this.shouldLoopAudio.set(false);
      return;
    }

    // Calculate total slideshow duration using 4 seconds per image (matching slideshow-control.service.ts)
    let totalSlideshowDuration = 0;
    mediaItems.forEach(item => {
      if (item.type === 'video' && item.duration) {
        totalSlideshowDuration += item.duration;
      } else {
        // For images, use 4 seconds to match the actual slideshow timing
        totalSlideshowDuration += 4; // seconds per image
      }
    });

    // Get audio duration
    const audioDuration = this.audioService.duration();

    // Smart looping logic:
    // - Don't loop if audio is very short compared to slideshow (less than 50% of slideshow duration)
    // - Don't loop if we have many images and short audio (would loop too frequently)
    // - Only loop if audio duration is reasonable compared to slideshow duration
    const audioToSlideshowRatio = audioDuration / totalSlideshowDuration;
    const imageCount = mediaItems.filter(item => item.type === 'image').length;

    const shouldLoop = totalSlideshowDuration > audioDuration &&
      audioToSlideshowRatio > 0.5 && // Audio should be at least 50% of slideshow duration
      !(imageCount > 2 && audioDuration < 15); // Don't loop short audio with many images

    this.shouldLoopAudio.set(shouldLoop);

    console.log(`Slideshow duration: ${totalSlideshowDuration}s, Audio duration: ${audioDuration}s, Ratio: ${audioToSlideshowRatio.toFixed(2)}, Images: ${imageCount}, Should loop: ${shouldLoop}`);
  }

  /**
   * Start monitoring audio playback for looping and state management
   */
  startAudioMonitoring(): void {
    // Stop any existing monitoring
    this.stopAudioMonitoring();

    console.log('Starting audio monitoring with EXACT 500ms intervals');
    this.audioMonitorInterval = setInterval(() => {
      // Monitor the current time and audio playing state
      const isPlaying = this.audioService.isPlaying();
      const currentTime = this.audioService.currentTime();
      const duration = this.audioService.duration();
      const endTime = this.audioService.endTime();

      // Audio looping is now handled by the slide-show component effect
      // This prevents conflicts between multiple looping systems

      // If audio is paused, make sure videos are also paused
      if (!isPlaying && this.isVideoPlaying()) {
        // Emit event to pause current video - this will need to be handled by parent
        console.log('Audio paused, should pause video');
      } else if (isPlaying && this.isVideoPlaying()) {
        // If audio is playing but video might be paused, resume video
        console.log('Audio playing, should resume video if paused');
      }
    }, 500); // Check every 500ms as per original implementation
  }

  /**
   * Stop audio monitoring
   */
  stopAudioMonitoring(): void {
    if (this.audioMonitorInterval) {
      console.log('Stopping audio monitoring');
      clearInterval(this.audioMonitorInterval);
      this.audioMonitorInterval = null;
    }
  }

  /**
   * Start heartbeat check for state synchronization
   * Detects and fixes UI/audio state mismatches every 2 seconds
   */
  startHeartbeatCheck(): void {
    // Stop any existing heartbeat
    this.stopHeartbeatCheck();

    console.log('Starting heartbeat check for state synchronization');
    this.heartbeatCheckInterval = setInterval(() => {
      // Get actual playback state from audio service
      const actuallyPlaying = this.audioService.isPlaying();
      const actuallyLoading = this.audioService.isLoading();
      const trackChanged = this.audioService.currentTrackIndex();

      // Check if UI controls reflect actual playing state
      // Don't check during transitions as they are intentionally temporary mismatches
      if (!this.isTransitioning()) {
        // Only check for genuine state mismatches, not normal pause/play operations
        // A mismatch occurs when:
        // 1. Audio is playing but UI shows user paused (audio started without UI update)
        // 2. Audio is paused, UI shows not paused, and we're not loading (audio stopped without UI update)
        const genuineMismatch = (actuallyPlaying && this.userPaused()) ||
          (!actuallyPlaying && !this.userPaused() && !actuallyLoading);

        // However, we should NOT "fix" normal user-initiated pause operations
        // Only fix if there's a genuine state desync that wasn't user-initiated
        if (genuineMismatch) {
          // Only log for debugging, don't automatically "fix" user pause states
          console.log(`Heartbeat: Potential state mismatch detected - ` +
            `Audio actually ${actuallyPlaying ? 'playing' : 'paused'}, ` +
            `UI userPaused: ${this.userPaused()}, Loading: ${actuallyLoading}`);

          // Don't automatically fix - let user interactions control the state
          // This prevents the pause/restart bug
        }
      }

      // Heartbeat check completed - no automatic state fixes to prevent pause/restart issues
    }, 2000); // Check every 2 seconds
  }

  /**
   * Stop heartbeat check
   */
  stopHeartbeatCheck(): void {
    if (this.heartbeatCheckInterval) {
      console.log('Stopping heartbeat check');
      clearInterval(this.heartbeatCheckInterval);
      this.heartbeatCheckInterval = null;
    }
  }

  /**
   * Reset slideshow state
   */
  resetSlideshow(): void {
    console.log('Resetting slideshow state');
    this.pauseSlideshow();
    this.isTransitioning.set(false);
    this.isVideoPlaying.set(false);
    this.userPaused.set(false);
  }

  /**
   * Set slideshow interval duration
   * @param duration Duration in milliseconds
   */
  setSlideshowInterval(duration: number): void {
    this.slideshowIntervalDuration.set(duration);
  }

  /**
   * Set user paused state
   * @param paused Whether user has manually paused
   */
  setUserPaused(paused: boolean): void {
    this.userPaused.set(paused);
    // If user is manually controlling playback, clear the tab switch flag
    if (!paused) {
      this.pausedDueToTabSwitch = false;
    }
  }

  /**
   * Reset tab switch pause flag - call this when user manually interacts with playback
   */
  clearTabSwitchFlag(): void {
    this.pausedDueToTabSwitch = false;
  }

  /**
   * Set video playing state
   * @param playing Whether a video is currently playing
   */
  setVideoPlaying(playing: boolean): void {
    this.isVideoPlaying.set(playing);
  }

  /**
   * Set transitioning state
   * @param transitioning Whether slideshow is transitioning
   */
  setTransitioning(transitioning: boolean): void {
    this.isTransitioning.set(transitioning);
  }

  /**
   * Handle page visibility changes for proper audio management
   */
  private setupPageVisibilityHandlers(): void {
    // Handle page visibility change
    this.visibilityHandler = () => {
      if (document.visibilityState === 'hidden') {
        console.log('Page hidden - pausing audio (preserving audio instance)');
        this.wasPlayingBeforeHidden = this.audioService.isPlaying();
        if (this.wasPlayingBeforeHidden) {
          // Use togglePlayPause to pause instead of stop - this preserves the audio instance
          this.audioService.togglePlayPause();
          // Set flags to track this was due to tab switching
          this.pausedDueToTabSwitch = true;
          this.setUserPaused(true);
        }
      } else if (document.visibilityState === 'visible') {
        console.log('Page visible again - audio was playing before:', this.wasPlayingBeforeHidden);
        // If the pause was due to tab switching, clear the userPaused flag to allow manual resume
        if (this.pausedDueToTabSwitch && this.wasPlayingBeforeHidden) {
          console.log('Clearing userPaused flag - audio ready for manual resume after tab switch');
          this.setUserPaused(false);
          this.pausedDueToTabSwitch = false;
        }
        // Don't auto-resume - user must manually click play
        // But the audio instance is preserved and ready for manual play
      }
    };

    // Handle page hide event (more reliable than visibilitychange on some mobile browsers)
    this.pageHideHandler = () => {
      console.log('Page hide event - pausing audio (preserving for return)');
      const wasPlaying = this.audioService.isPlaying();
      if (wasPlaying) {
        // Pause instead of stop to preserve audio instance
        this.audioService.togglePlayPause();
        this.pausedDueToTabSwitch = true;
        this.setUserPaused(true);
      }
    };

    this.beforeUnloadHandler = () => {
      console.log('Page unloading - cleanup (page actually closing)');
      // Only do full cleanup when the page is actually being closed/refreshed
      this.audioService.cleanup();
    };

    // Attach event listeners
    document.addEventListener('visibilitychange', this.visibilityHandler);
    window.addEventListener('pagehide', this.pageHideHandler);
    window.addEventListener('beforeunload', this.beforeUnloadHandler);

    console.log('Page visibility handlers initialized with pause-preserve strategy');
  }

  /**
   * Remove page visibility and unload event handlers
   */
  private removePageVisibilityHandlers(): void {
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }

    if (this.pageHideHandler) {
      window.removeEventListener('pagehide', this.pageHideHandler);
      this.pageHideHandler = null;
    }

    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }
  }
}
